import React, { useCallback, useEffect, useState } from 'react';
import { Copy, KeyRound, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useApi from '../api';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { apiErrorMessage, formatRelativeTime } from './servicesShared';

interface SdkPublishableKey {
    id: string;
    environment: 'test' | 'live';
    name: string;
    prefix: string;
    status: 'active' | 'revoked';
    last_used_at?: string | null;
    created_at?: string | null;
    revoked_at?: string | null;
}

interface RevealedKey extends SdkPublishableKey {
    publishable_key: string;
    rotated_from?: string;
    previous_key_remains_active?: boolean;
}

const ServiceSdkKeysTab: React.FC<{ active: boolean }> = ({ active }) => {
    const { get, post, remove } = useApi();
    const [keys, setKeys] = useState<SdkPublishableKey[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('Web app');
    const [environment, setEnvironment] = useState<'test' | 'live'>('test');
    const [revealed, setRevealed] = useState<RevealedKey | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const response = await get('/services/sdk-keys');
            setKeys(response.data.data || []);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to load SDK keys'));
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        if (active) load();
    }, [active, load]);

    const create = async () => {
        if (!name.trim()) {
            toast.error('Give the key a name');
            return;
        }
        try {
            setSaving(true);
            const response = await post('/services/sdk-keys', {
                environment,
                name: name.trim(),
            });
            setRevealed(response.data);
            await load();
            toast.success('Publishable key created');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to create SDK key'));
        } finally {
            setSaving(false);
        }
    };

    const rotate = async (key: SdkPublishableKey) => {
        try {
            const response = await post(`/services/sdk-keys/${key.id}/rotate`, {});
            setRevealed(response.data);
            await load();
            toast.success('Replacement key created; the previous key remains active');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to rotate SDK key'));
        }
    };

    const revoke = async (key: SdkPublishableKey) => {
        if (!window.confirm(`Revoke ${key.name} (${key.prefix}…)? New and current browser sessions using it will stop working.`)) {
            return;
        }
        try {
            await remove(`/services/sdk-keys/${key.id}`);
            await load();
            toast.success('SDK key revoked');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to revoke SDK key'));
        }
    };

    const copy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            toast.success('Copied publishable key');
        } catch {
            toast.error('Could not copy publishable key');
        }
    };

    return (
        <div className="space-y-4">
            <Alert>
                <ShieldCheck className="h-4 w-4" />
                <AlertTitle>Public by design</AlertTitle>
                <AlertDescription>
                    A publishable key identifies this organization and environment; it grants no service access. Your backend signs short-lived user assertions, and Aimdoc still evaluates every action through the offering access policy.
                </AlertDescription>
            </Alert>

            {revealed && (
                <Alert className="border-green-500/40 bg-green-500/5">
                    <KeyRound className="h-4 w-4" />
                    <AlertTitle>Copy this key now</AlertTitle>
                    <AlertDescription className="space-y-3">
                        <p>Aimdoc stores only its digest, so the complete value is shown once.</p>
                        <div className="flex items-center gap-2">
                            <code className="min-w-0 flex-1 overflow-x-auto rounded bg-muted px-3 py-2 text-xs">
                                {revealed.publishable_key}
                            </code>
                            <Button variant="outline" size="sm" onClick={() => copy(revealed.publishable_key)}>
                                <Copy className="mr-1 h-4 w-4" /> Copy
                            </Button>
                        </div>
                        {revealed.previous_key_remains_active && (
                            <p>The prior key remains active for a zero-downtime rollout. Revoke it after customers deploy the replacement.</p>
                        )}
                    </AlertDescription>
                </Alert>
            )}

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Create a publishable key</CardTitle>
                    <CardDescription>Use test keys during integration and live keys in production.</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-end gap-3">
                    <div className="min-w-52 space-y-2">
                        <Label htmlFor="sdk-key-name">Name</Label>
                        <Input
                            id="sdk-key-name"
                            value={name}
                            maxLength={100}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Customer portal"
                        />
                    </div>
                    <div className="w-40 space-y-2">
                        <Label>Environment</Label>
                        <Select value={environment} onValueChange={(value) => setEnvironment(value as 'test' | 'live')}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="test">Test</SelectItem>
                                <SelectItem value="live">Live</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={create} disabled={saving}>
                        <Plus className="mr-1 h-4 w-4" /> {saving ? 'Creating…' : 'Create key'}
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Keys</CardTitle>
                    <CardDescription>Rotation creates an overlapping key. Revocation is explicit.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                    {loading ? (
                        <div className="h-20 animate-pulse rounded bg-muted" />
                    ) : keys.length === 0 ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">No SDK keys yet.</p>
                    ) : keys.map((key) => (
                        <div key={key.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">{key.name}</span>
                                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase ${key.environment === 'live' ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-blue-500/10 text-blue-700 dark:text-blue-400'}`}>
                                        {key.environment}
                                    </span>
                                    {key.status === 'revoked' && <span className="text-xs text-muted-foreground">Revoked</span>}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                                    <code>{key.prefix}…</code>
                                    <span>Created {formatRelativeTime(key.created_at)}</span>
                                    <span>{key.last_used_at ? `Used ${formatRelativeTime(key.last_used_at)}` : 'Never used'}</span>
                                </div>
                            </div>
                            {key.status === 'active' && (
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => rotate(key)}>
                                        <RefreshCw className="mr-1 h-4 w-4" /> Rotate
                                    </Button>
                                    <Button variant="outline" size="sm" className="text-destructive" onClick={() => revoke(key)}>
                                        <Trash2 className="mr-1 h-4 w-4" /> Revoke
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
};

export default ServiceSdkKeysTab;
